'use strict';

(() => {

  /* ======================================================================
   * Utilidades básicas
   * ====================================================================== */
  const D = document;
  const W = window;

  const $   = (selector, root = D) => root.querySelector(selector);
  const $$  = (selector, root = D) => Array.from(root.querySelectorAll(selector));
  const on  = (el, type, fn, opts) => el && el.addEventListener(type, fn, opts);
  const kids = (el, tag) => Array.from(el?.children || []).filter(n => n.tagName?.toLowerCase() === tag);

  const ready = (fn) => {
    if (D.readyState !== 'loading') fn();
    else on(D, 'DOMContentLoaded', fn);
  };

  const lockScroll = (shouldLock) => {
    const body = D.body;
    if (!body) return;
    body.toggleAttribute('data-no-scroll', shouldLock);
    body.style.overflow = shouldLock ? 'hidden' : '';
    if (!shouldLock) body.style.overflowY = '';
  };

  /* ======================================================================
   * 1) Menú de navegación (mobile / desktop)
   * ====================================================================== */
  const MOBILE = '(max-width: 640px)';

  function initNav() {
    const header       = $('header');
    const navListItems = $$('header > nav.isLinks > ul > li');
    const mobile       = W.matchMedia(MOBILE);

    const syncMenuState = () => {
      if (!header) return;
      const open = !!$('header > nav.isLinks > ul > li.isOpen');
      header.classList.toggle('isMenu', open && !mobile.matches);
    };

    const removeAllOpen = () => {
      navListItems.forEach(li => li.classList.remove('isOpen'));
      syncMenuState();
    };

    navListItems.forEach(item => {
      on(item, 'mouseenter', () => {
        if (mobile.matches) return;
        removeAllOpen();
        item.classList.add('isOpen');
        syncMenuState();
      });

      on(item, 'mouseleave', () => {
        if (mobile.matches) return;
        item.classList.remove('isOpen');
        syncMenuState();
      });
    });

    on(mobile, 'change', removeAllOpen);
  }

  /* ======================================================================
   * 1.2) Drawer: menú mobile con niveles
   * ====================================================================== */
  function initDrawer() {
    const header  = $('header');
    const drawer  = $('drawer', header || D);
    const trigger = $('.setMenu', header || D);
    if (!drawer) return;

    const panels = $$('panel', drawer);
    const stack  = ['root'];

    const render = () => {
      const current = stack[stack.length - 1];
      panels.forEach(panel => {
        const id = panel.dataset.panel;
        panel.classList.toggle('isActive', id === current);
        panel.classList.toggle('isPast', id !== current && stack.indexOf(id) !== -1);
        if (id === current) panel.scrollTop = 0;
      });
    };

    const goTo = (id) => {
      if (!panels.some(panel => panel.dataset.panel === id)) return;
      if (stack[stack.length - 1] === id) return;
      stack.push(id);
      render();
    };

    const goBack = () => {
      if (stack.length < 2) return false;
      stack.pop();
      render();
      return true;
    };

    const openDrawer = () => {
      drawer.classList.add('isOpen');
      drawer.setAttribute('aria-hidden', 'false');
      trigger?.setAttribute('aria-expanded', 'true');
      lockScroll(true);
    };

    const closeDrawer = () => {
      drawer.classList.remove('isOpen');
      drawer.setAttribute('aria-hidden', 'true');
      trigger?.setAttribute('aria-expanded', 'false');
      lockScroll(false);
      setTimeout(() => {
        stack.length = 1;
        render();
      }, 350);
    };

    W.AR33 = W.AR33 || {};
    W.AR33.openDrawer  = openDrawer;
    W.AR33.closeDrawer = closeDrawer;

    on(trigger, 'click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (drawer.classList.contains('isOpen')) closeDrawer();
      else openDrawer();
    });

    on(trigger, 'keydown', (e) => {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      e.preventDefault();
      openDrawer();
    });

    on(drawer, 'click', (e) => {
      if (e.target.closest('.setExit')) {
        e.preventDefault();
        closeDrawer();
        return;
      }

      if (e.target.closest('.setBack')) {
        e.preventDefault();
        goBack();
        return;
      }

      const next = e.target.closest('[data-goto]');
      if (next) {
        e.preventDefault();
        goTo(next.dataset.goto);
        return;
      }

      if (e.target.closest('a[href]')) closeDrawer();
    });

    on(D, 'keydown', (e) => {
      if (e.key !== 'Escape' || !drawer.classList.contains('isOpen')) return;
      if (!goBack()) closeDrawer();
    });

    on(W.matchMedia(MOBILE), 'change', (e) => {
      if (!e.matches && drawer.classList.contains('isOpen')) closeDrawer();
    });

    render();
  }

  /* ======================================================================
   * 1.1) Header: contraste dinámico, blur y offset del primer bloque
   * ====================================================================== */
  function initHeaderTheme() {
    const header = $('header');
    if (!header) return;

    const MEDIA  = 'img,picture,video,iframe,canvas';
    const LIMIT  = 0.62;

    const readLuma = (color) => {
      const parts = String(color).match(/[\d.]+/g);
      if (!parts) return null;
      const alpha = parts.length > 3 ? parseFloat(parts[3]) : 1;
      if (alpha < 0.5) return null;
      const r = Number(parts[0]);
      const g = Number(parts[1]);
      const b = Number(parts[2]);
      return (r * 0.299 + g * 0.587 + b * 0.114) / 255;
    };

    const cache = new WeakMap();

    const targetAt = (x, y) => {
      const stack = D.elementsFromPoint(x, y) || [];
      for (const el of stack) {
        if (!el || el === D.body || el === D.documentElement) continue;
        if (el.closest('header, notice, aside')) continue;
        return el;
      }
      return null;
    };

    const scopeOf = (el) => el && (el.closest('[data-header]') || el.closest('.shopify-section'));

    const toneAt = (x, y) => {
      const stack = D.elementsFromPoint(x, y) || [];
      for (const el of stack) {
        if (!el || el === D.body || el === D.documentElement) continue;
        if (el.closest('header, notice, aside')) continue;

        const flag = el.closest('[data-header]');
        if (flag) return flag.getAttribute('data-header') === 'dark' ? 1 : 0;

        if (el.matches(MEDIA)) return 1;

        const luma = readLuma(getComputedStyle(el).backgroundColor);
        if (luma !== null) return luma < LIMIT ? 1 : 0;
      }
      return 0;
    };

    const readTone = () => {
      const box   = header.getBoundingClientRect();
      const y     = Math.max(1, Math.min(W.innerHeight - 1, box.top + box.height / 2));
      const w     = W.innerWidth;
      const xs    = [w * 0.08, w * 0.5, w * 0.92].map(x => Math.max(1, Math.min(w - 1, x)));
      const scope = scopeOf(targetAt(xs[1], y)) || scopeOf(targetAt(xs[0], y));

      if (scope) {
        const flag = scope.getAttribute('data-header');
        if (flag) return flag === 'dark';
        if (cache.has(scope)) return cache.get(scope);
      }

      let dark = 0;
      xs.forEach(x => { dark += toneAt(x, y); });

      const tone = dark >= 2;
      if (scope) cache.set(scope, tone);
      return tone;
    };

    const setPush = () => {
      const first = $$('.shopify-section').find(el => !el.querySelector('header'));
      if (!first) return;
      const media = first.querySelector('slider, picture, video');
      const box   = media && media.getBoundingClientRect();
      const hero  = !!box && box.height > W.innerHeight * 0.5;
      first.classList.toggle('isPush', !hero);
    };

    let ticking = false;

    const reset = () => {
      $$('[data-header], .shopify-section').forEach(el => cache.delete(el));
    };

    const update = () => {
      ticking = false;
      header.classList.toggle('isBlur', W.scrollY > W.innerHeight * 0.1);
      header.classList.toggle('isDark', readTone());
    };

    const request = () => {
      if (ticking) return;
      ticking = true;
      W.requestAnimationFrame(update);
    };

    on(W, 'scroll', request, { passive: true });
    on(W, 'resize', () => { reset(); setPush(); request(); });
    on(W, 'load',   () => { reset(); setPush(); request(); });
    on(D, 'transitionend', (e) => {
      if (e.target?.classList?.contains('swiper-wrapper')) { reset(); request(); }
    }, true);

    setPush();
    update();
  }

  /* ======================================================================
   * 1.2) Header: contador del carrito
   * ====================================================================== */
  function initCartBadge() {
    const badges = $$('header [data-cart-count]');
    if (!badges.length) return;

    const source = badges[0];

    const sync = () => {
      const total = parseInt(source.textContent, 10) || 0;
      badges.forEach(badge => {
        if (badge !== source) badge.textContent = String(total);
        badge.classList.toggle('isZero', total < 1);
      });
    };

    sync();
    new MutationObserver(sync).observe(source, { childList: true, characterData: true, subtree: true });
  }

  /* ======================================================================
   * 3) Aside
   * ====================================================================== */
  function initAsideToggles() {

    const showAllItems = (aside) => {
      kids(aside, 'item').forEach(el => {
        el.hidden        = false;
        el.style.display = 'block';
      });
    };

    const openAside = (asideId, accordionId) => {
      const aside = $('aside');
      if (!aside || !asideId) return;

      aside.classList.add('isOpen');
      lockScroll(true);

      const items = kids(aside, 'item').filter(el => el.hasAttribute('id'));

      if (items.length) {
        const exists = items.some(el => el.id === asideId);
        items.forEach(el => {
          const active     = !exists || el.id === asideId;
          el.hidden        = !active;
          el.style.display = active ? 'block' : 'none';
        });
      }

      if (accordionId) {
        setTimeout(() => {
          let dt = D.getElementById(accordionId);
          if (!dt) return;
          if (dt.tagName.toLowerCase() !== 'dt') {
            dt = dt.querySelector('dt') || dt.closest('dt') || dt;
          }

          const dl  = dt.closest('dl');
          const dts = dl ? kids(dl, 'dt') : [dt];
          const dds = dl ? kids(dl, 'dd') : [];

          dts.forEach(d => d.classList.remove('isActive'));
          dds.forEach(x => { x.style.transition = 'none'; x.style.maxHeight = null; });

          const dd = dt.nextElementSibling;
          if (dd && dd.tagName.toLowerCase() === 'dd') {
            dd.style.transition = 'none';
            dd.style.maxHeight  = dd.scrollHeight + 'px';
            requestAnimationFrame(() => { dd.style.transition = ''; });
          }
          dt.classList.add('isActive');
        }, 50);
      }

      console.log('[Aside] Abierto →', asideId, accordionId ? `| Acordeón: ${accordionId}` : '');
    };

    const closeAside = (aside) => {
      const el = aside || $('aside');
      if (!el) return;
      el.classList.remove('isOpen');
      lockScroll(false);
      showAllItems(el);
    };

    W.AR33         = W.AR33 || {};
    W.AR33.openAside  = openAside;
    W.AR33.closeAside = closeAside;

    on(D, 'click', (e) => {
      const aside = $('aside');
      if (!aside) return;

      const t = e.target;

      if (t === aside || t.closest('a[href="#Close"]')) {
        closeAside(aside);
        e.preventDefault();
        return;
      }

      const link = t.closest('a[href^="open#"]');
      if (!link) return;

      const raw      = link.getAttribute('href').replace(/^open/, '');
      const fragment = raw.startsWith('#') ? raw.slice(1) : raw;
      const ampIdx   = fragment.indexOf('&');
      const asideId  = ampIdx !== -1 ? fragment.slice(0, ampIdx) : fragment;
      const accId    = ampIdx !== -1 ? fragment.slice(ampIdx + 1) : null;

      if (!asideId) return;

      openAside(asideId, accId);
      e.preventDefault();
    });
  }

  /* ======================================================================
   * 4) Links externos
   * ====================================================================== */
  function enhanceExternalLinks() {
    for (const a of $$('a[href]')) {
      let url;
      try { url = new URL(a.getAttribute('href'), location.href); } catch { continue; }
      if ((url.protocol === 'http:' || url.protocol === 'https:') && url.origin !== location.origin) {
        a.target = '_blank';
        a.rel    = 'noopener noreferrer';
      }
    }
  }

  /* ======================================================================
   * 5) List/Grid toggle
   * ====================================================================== */
  function initListGridToggle() {
    const list = $('.isType');
    if (!list) return;

    const allGrids    = $$('grid');
    const nearestGrid = (from) => {
      let node = from;
      while (node) {
        const parent = node.parentElement;
        if (!parent) break;
        const grids = Array.from(parent.querySelectorAll('grid'));
        if (grids.length) {
          const siblings  = Array.from(parent.children);
          const iFrom     = siblings.indexOf(node);
          let best = null, bestDelta = Infinity;
          for (const g of grids) {
            let top = g;
            while (top && top.parentElement !== parent) top = top.parentElement;
            const d = Math.abs(siblings.indexOf(top || g) - iFrom);
            if (d < bestDelta) { bestDelta = d; best = g; }
          }
          if (best) return best;
        }
        node = parent;
      }
      return allGrids[0] || null;
    };

    const grid = nearestGrid(list);
    if (!grid) return;

    const key = 'gridMode:' + (grid.dataset.cacheKey || grid.id || ('idx:' + allGrids.indexOf(grid) + '@' + location.pathname));

    try {
      const saved = localStorage.getItem(key);
      if (saved === '0' || saved === '1') grid.classList.toggle('isAlt', saved === '1');
    } catch {}

    on(list, 'click', (e) => {
      const li = e.target.closest('li');
      if (!li || !list.contains(li)) return;
      list.querySelector('li.isActive')?.classList.remove('isActive');
      li.classList.add('isActive');
      const makeAlt = li.dataset.type === '1';
      grid.classList.toggle('isAlt', makeAlt);
      try { localStorage.setItem(key, makeAlt ? '1' : '0'); } catch {}
    });
  }

  /* ======================================================================
   * 6) Toggles de atributos
   * ====================================================================== */
  function initAttributeToggles() {
    const wrap = $('trigger');
    if (!wrap) return;

    const triggers  = $$('item', wrap);
    if (!triggers.length) return;

    const groups    = $$('toggles').map(w => $$('item', w));
    const hasGroups = groups.some(g => g.length);
    const N         = triggers.length;

    const parsePairs = (s) =>
      !s ? [] : s.split(';').map(x => x.trim()).filter(Boolean).map(p => {
        const i = p.indexOf('=');
        return i < 0 ? [p.trim(), ''] : [p.slice(0, i).trim(), p.slice(i + 1).trim()];
      }).filter(([k]) => k);

    const parseList = (s) => !s ? [] : s.split(',').map(x => x.trim()).filter(Boolean);

    const allNodes = () => [D.documentElement, D.body, ...(D.body ? D.body.querySelectorAll('*') : [])].filter(Boolean);

    const apply = (setPairs, rmList) => {
      const nodes = allNodes();
      if (rmList?.length)  for (const el of nodes) for (const name of rmList) try { el.removeAttribute(name); } catch {}
      if (setPairs?.length) for (const el of nodes) for (const [k, v] of setPairs) try {
        if (v === '' || v?.toLowerCase?.() === 'null') el.removeAttribute(k);
        else if (el.getAttribute(k) !== v) el.setAttribute(k, v);
      } catch {}
    };

    const setActive = (i) => {
      triggers.forEach(el => el.classList.remove('isActive'));
      triggers[i]?.classList.add('isActive');

      if (hasGroups) {
        for (const g of groups) {
          g.forEach((el, idx) => { el.style.display = (idx % N) === i ? 'block' : 'none'; });
        }
      }

      const t = triggers[i];
      if (!t) return;
      const setPairs = parsePairs(t.dataset.attrs);
      const rm       = parseList(t.dataset.remove);
      'requestIdleCallback' in W
        ? requestIdleCallback(() => apply(setPairs, rm), { timeout: 200 })
        : apply(setPairs, rm);
    };

    setActive(0);

    on(wrap, 'click', (e) => {
      const it  = e.target.closest('item');
      if (!it || !wrap.contains(it)) return;
      const idx = triggers.indexOf(it);
      if (idx >= 0) setActive(idx);
    });
  }

  /* ======================================================================
   * 7) Acordeón
   * ====================================================================== */
  function initAccordion() {
    for (const dl of $$('dl')) {
      const dts = kids(dl, 'dt');
      const dds = kids(dl, 'dd');

      dts.forEach(d => d.classList.remove('isActive'));
      dds.forEach(dd => (dd.style.maxHeight = null));

      on(dl, 'click', (e) => {
        const dt = e.target.closest('dt');
        if (!dt || !dl.contains(dt)) return;

        const dd        = dt.nextElementSibling;
        const wasActive = dt.classList.contains('isActive');

        dts.forEach(d => d.classList.remove('isActive'));
        dds.forEach(x => (x.style.maxHeight = null));

        if (!wasActive && dd && dd.tagName.toLowerCase() === 'dd') {
          dt.classList.add('isActive');
          dd.style.maxHeight = dd.scrollHeight + 'px';
        }
      });
    }
  }

  /* ======================================================================
   * 8) Galería
   * ====================================================================== */
  function initGallery() {
    const photos = $$('photo > item');
    const thumbs = $$('thumb > item');
    if (!photos.length || !thumbs.length) return;

    let active = -1;
    const setActive = (i) => {
      if (i === active) return;
      thumbs[active]?.classList?.remove('isActive');
      thumbs[i]?.classList?.add('isActive');
      active = i;
    };

    const thumbWrap = $('thumb');
    if (thumbWrap) {
      on(thumbWrap, 'click', (e) => {
        const t = e.target.closest('item');
        if (!t || !thumbWrap.contains(t)) return;
        const i = thumbs.indexOf(t);
        if (i < 0) return;
        setActive(i);
        photos[i]?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
      });
    }

    if ('IntersectionObserver' in W) {
      const index = new Map(photos.map((el, i) => [el, i]));
      const io    = new IntersectionObserver((entries) => {
        let best = -1, bestRatio = 0;
        for (const en of entries) {
          if (en.isIntersecting && en.intersectionRatio > bestRatio) {
            bestRatio = en.intersectionRatio;
            best      = index.get(en.target);
          }
        }
        if (best > -1) setActive(best);
      }, { threshold: 0.5 });
      photos.forEach(el => { if (el?.nodeType === 1) io.observe(el); });
    } else {
      setActive(0);
    }
  }

  /* ======================================================================
   * 9) Swipers
   * ====================================================================== */
  function initSwipers() {
    if (typeof W.Swiper !== 'function') return;

    D.querySelectorAll('.swiper').forEach((el) => {
      if (!el || el.nodeType !== 1) return;

      const ds  = el.dataset || {};
      const min = Number(ds.min) || 1;
      const max = Number(ds.max) || min;

      const prevEl     = el.querySelector('prev') || null;
      const nextEl     = el.querySelector('next') || null;
      const navigation = (prevEl || nextEl) ? { prevEl, nextEl } : undefined;

      const config = {
        centeredSlidesBounds: true,
        centeredSlides:       ds.half === 'true',
        slidesPerView:        min,
        observer:             true,
        loop:                 ds.loop === 'true',
        speed:                750,
        autoHeight:           true,
        autoplay:             ds.play === 'true' ? { delay: 4500, disableOnInteraction: false } : false,
        breakpoints: { 640: { slidesPerView: min }, 1024: { slidesPerView: max } }
      };
      if (navigation) config.navigation = navigation;

      try { new Swiper(el, config); }
      catch (err) { console.warn('⚠️ Swiper error:', err?.message || err); }
    });
  }

  /* ======================================================================
   * 10) Grid AJAX (Ver más)
   * ====================================================================== */
  function initAjaxGrid() {
    const grid = D.querySelector('grid[data-items]');
    const btn  = D.querySelector('a[data-ajax]');
    if (!grid || !btn) return;

    const BATCH = parseInt(grid.dataset.items, 10) || 8;

    let localItems = kids(grid, 'items')[0];
    if (!localItems) { localItems = D.createElement('items'); grid.appendChild(localItems); }

    btn.dataset.page = btn.dataset.page || '1';
    btn.dataset.busy = '0';

    const setBusy = (busy) => {
      btn.textContent = busy ? (W.AR33_i18n?.loading || 'cargando') : (W.AR33_i18n?.loadMore || 'Ver más');
      btn.dataset.busy = busy ? '1' : '0';
      btn.setAttribute('aria-disabled', busy ? 'true' : 'false');
      btn.style.pointerEvents = busy ? 'none' : '';
    };

    const nextURL = () => {
      const base     = new URL(btn.href, location.origin);
      const nextPage = (parseInt(btn.dataset.page, 10) || 1) + 1;
      base.searchParams.set('page', String(nextPage));
      return { url: base.toString(), nextPage };
    };

    const extractItems = (doc) => {
      let remote = doc.querySelector('grid > items');
      if (!remote) {
        const rg = doc.querySelector('grid[data-items], grid, [data-products], .grid, .product-grid');
        remote = (rg ? kids(rg, 'items')[0] : null) || rg;
      }
      if (!remote) return [];
      return Array.from(remote.children).filter(n => n.nodeType === 1).slice(0, BATCH);
    };

    on(btn, 'click', async (ev) => {
      ev.preventDefault();
      if (btn.dataset.busy === '1') return;
      setBusy(true);
      try {
        const { url, nextPage } = nextURL();
        const res   = await fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const items = extractItems(new DOMParser().parseFromString(await res.text(), 'text/html'));
        if (!items.length) { btn.remove(); return; }
        const frag = D.createDocumentFragment();
        items.forEach(el => frag.appendChild(el));
        localItems.appendChild(frag);
        btn.dataset.page = String(nextPage);
        setBusy(false);
      } catch (e) {
        console.error('❌ AJAX grid error:', e);
        btn.textContent = (W.AR33_i18n?.error || 'Error');
        setBusy(false);
      }
    });
  }

  /* ======================================================================
   * 11) Recomendaciones lazy
   * ====================================================================== */
  function initRecommendations() {
    const SEL     = '[data-loader]';
    const section = $(SEL);
    if (!section || section.dataset.loaded === 'true') return;

    const url = section.dataset.url;
    if (!url) return;

    const load = async () => {
      if (section.dataset.loaded === 'true') return;
      section.dataset.loaded = 'true';
      const ctrl      = new AbortController();
      const timeoutId = setTimeout(() => ctrl.abort(), 8000);
      try {
        const res      = await fetch(url, { headers: { Accept: 'text/html' }, signal: ctrl.signal });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const incoming = new DOMParser().parseFromString(await res.text(), 'text/html').querySelector(SEL);
        if (incoming?.innerHTML?.trim()) {
          requestAnimationFrame(() => {
            const frag = D.createDocumentFragment();
            Array.from(incoming.childNodes).forEach(n => frag.appendChild(n));
            section.replaceChildren(frag);
            delete section.dataset.url;
            initSwipers();
          });
        }
      } catch (err) {
        console.error('❌ Recommendations error:', err?.name === 'AbortError' ? 'timeout' : err);
        delete section.dataset.loaded;
      } finally {
        clearTimeout(timeoutId);
      }
    };

    if ('IntersectionObserver' in W) {
      const io = new IntersectionObserver((entries) => {
        for (const en of entries) {
          if (en.isIntersecting) { io.unobserve(en.target); load(); break; }
        }
      }, { root: null, threshold: 0.01, rootMargin: '400px 0px' });
      io.observe(section);
    } else {
      load();
    }
  }

  /* ======================================================================
   * 12) isCrop — Texto expandible
   * ====================================================================== */
  function initCrop() {
    $$('.isCrop').forEach(el => {
      const max   = parseInt(el.dataset.max, 10) || 200;
      const lMore = el.dataset.open  || (W.AR33_i18n?.readMore || 'Seguir Leyendo');
      const lLess = el.dataset.close || (W.AR33_i18n?.close || 'Cerrar');
      const full  = el.textContent.trim();
      if (full.length <= max) return;

      const original = el.innerHTML;
      const btn = Object.assign(D.createElement('a'), { className: 'isCrop-toggle', href: '#' });

      const collapse = () => {
        el.classList.remove('is-expanded');
        el.innerHTML = full.slice(0, max) + '\u2026 ';
        btn.textContent = lMore;
        el.appendChild(btn);
      };

      const expand = () => {
        el.classList.add('is-expanded');
        el.innerHTML = original + ' ';
        btn.textContent = lLess;
        el.appendChild(btn);
      };

      on(btn, 'click', (e) => { e.preventDefault(); el.classList.contains('is-expanded') ? collapse() : expand(); });
      collapse();
    });
  }

  function initSound() {
    const buttons = $$('sound[data-sound]');
    if (!buttons.length) return;

    const findVideo = (button) => $('video', button.closest('player') || button.parentElement);

    const silence = () => {
      $$('video').forEach(video => { video.muted = true; });
      buttons.forEach(button => {
        button.classList.remove('isActive');
        $('i', button)?.classList.replace('isP22', 'isP21');
      });
    };

    buttons.forEach(button => {
      const video = findVideo(button);
      if (!video) { button.remove(); return; }
      video.muted = true;
      video.setAttribute('muted', '');
    });

    buttons.forEach(button => {
      on(button, 'click', (e) => {
        e.preventDefault();
        e.stopPropagation();

        const video  = findVideo(button);
        const active = button.classList.contains('isActive');

        silence();
        if (active || !video) return;

        video.muted = false;
        video.removeAttribute('muted');
        video.volume = 1;
        if (video.paused) video.play().catch(() => {});

        button.classList.add('isActive');
        $('i', button)?.classList.replace('isP21', 'isP22');
      });
    });
  }

  /* ======================================================================
   * Init
   * ====================================================================== */
  ready(() => {
    initNav();
    initDrawer();
    initHeaderTheme();
    initCartBadge();
    initAsideToggles();
    enhanceExternalLinks();
    initListGridToggle();
    initAttributeToggles();
    initAccordion();
    initGallery();
    initSwipers();
    initAjaxGrid();
    initRecommendations();
    initCrop();
    initSound();
  });

})();